import { type ReactNode } from "react";
import type { ValueLabel } from "../types/ValueLabel";
import LargeSelectInputSelect from "./LargeSelectInputSelect";
import LargeSelectInputCombobox from "./LargeSelectInputCombobox";

interface Props<T extends string | number = string> {
  options: ValueLabel<T, string>[];
  value: T | ValueLabel<T, string> | null | undefined;
  label?: ReactNode;
  placeholder?: string;
  noResultsText?: string;
  disabled?: boolean;
  required?: boolean;
  helpTextAccordion?: ReactNode;
  clearable?: boolean;
  searchable?: boolean;
  fieldName?: string;
  showValidation?: boolean;
  setValue: (v: ValueLabel<T, string> | null | undefined) => void;
}

/**
 * Accessible select/combobox component wrapper
 *
 * Renders either LargeSelectInputSelect (for non-searchable dropdowns with useSelect)
 * or LargeSelectInputCombobox (for searchable dropdowns with useCombobox)
 */
export default function LargeSelectInput<T extends string | number = string>({
  options,
  value,
  label,
  placeholder,
  noResultsText,
  disabled = false,
  required = false,
  helpTextAccordion,
  clearable = false,
  searchable = false,
  fieldName,
  showValidation = false,
  setValue,
}: Readonly<Props<T>>) {
  if (searchable) {
    return (
      <LargeSelectInputCombobox
        options={options}
        value={value}
        label={label}
        placeholder={placeholder}
        noResultsText={noResultsText}
        disabled={disabled}
        required={required}
        helpTextAccordion={helpTextAccordion}
        clearable={clearable}
        fieldName={fieldName}
        showValidation={showValidation}
        setValue={setValue}
      />
    );
  }

  return (
    <LargeSelectInputSelect
      options={options}
      value={value}
      label={label}
      placeholder={placeholder}
      noResultsText={noResultsText}
      disabled={disabled}
      required={required}
      helpTextAccordion={helpTextAccordion}
      clearable={clearable}
      fieldName={fieldName}
      showValidation={showValidation}
      setValue={setValue}
    />
  );
}
